/**
 * FounderPulse  -  thin admin-only live metrics strip pinned to the very top
 * of every page. Only renders when the logged-in user has role=admin.
 *
 * Auto-refreshes every 60 seconds. Hideable for the session (sessionStorage).
 */
import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Activity, UserPlus, Sparkles, DollarSign, X } from "lucide-react";
import api from "@/lib/api";

const HIDE_KEY = "sl_founder_pulse_hidden";
const POLL_MS = 60_000;

function fmtUSD(n) {
  if (typeof n !== "number" || Number.isNaN(n)) return "$0.00";
  return n >= 100
    ? `$${n.toFixed(0)}`
    : `$${n.toFixed(2)}`;
}

export default function FounderPulse() {
  const { user } = useAuth();
  const [pulse, setPulse] = useState(null);
  const [hidden, setHidden] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (typeof window !== "undefined" && sessionStorage.getItem(HIDE_KEY) === "1") {
      setHidden(true);
    }
  }, []);

  useEffect(() => {
    if (!user || (user.role || "").toLowerCase() !== "admin" || hidden) return;
    let cancelled = false;
    const fetchPulse = async () => {
      try {
        const { data } = await api.get("/admin/pulse");
        if (!cancelled) { setPulse(data); setErr(null); }
      } catch (e) {
        if (!cancelled) setErr(e?.response?.status === 403 ? "forbidden" : "error");
      }
    };
    fetchPulse();
    const id = setInterval(fetchPulse, POLL_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, [user, hidden]);

  if (!user || (user.role || "").toLowerCase() !== "admin") return null;
  if (hidden || err === "forbidden") return null;
  if (!pulse) return null;

  const t = pulse.today || {};
  const dismiss = () => {
    sessionStorage.setItem(HIDE_KEY, "1");
    setHidden(true);
  };

  return (
    <div
      data-testid="founder-pulse-strip"
      role="region"
      aria-label="Co-founder mode live pulse"
      className="w-full bg-[#0F1830] text-white border-b border-[#E69500]/40 font-manrope text-[12px] sm:text-[13px]"
    >
      <div className="max-w-7xl mx-auto px-3 sm:px-6 py-1.5 flex items-center gap-3 sm:gap-6 overflow-x-auto whitespace-nowrap">
        <span
          className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.18em] text-[#F2CC8F] shrink-0"
          data-testid="founder-pulse-label"
        >
          <Activity className="h-3 w-3" /> Co-founder mode
        </span>

        <Stat
          icon={UserPlus}
          label="Sign-ups today"
          value={t.signups ?? 0}
          tip={`${pulse.last_7_days?.signups ?? 0} in the last 7d`}
          testid="founder-pulse-signups"
        />
        <Stat
          icon={Sparkles}
          label="SpectraMind generations today"
          value={t.ai_generations ?? 0}
          tip={`${pulse.last_7_days?.ai_generations ?? 0} in the last 7d`}
          testid="founder-pulse-generations"
        />
        <Stat
          icon={Activity}
          label="Active educators today"
          value={t.active_users ?? 0}
          testid="founder-pulse-active"
        />
        <Stat
          icon={DollarSign}
          label="Anthropic spend today"
          value={fmtUSD(t.anthropic_spend_usd)}
          tone={(t.anthropic_spend_usd || 0) > 25 ? "warn" : "neutral"}
          testid="founder-pulse-spend"
        />

        <button
          type="button"
          onClick={dismiss}
          aria-label="Hide co-founder pulse for this session"
          data-testid="founder-pulse-dismiss"
          className="ml-auto inline-flex items-center justify-center h-6 w-6 rounded-full text-white/60 hover:text-white hover:bg-white/10 shrink-0"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, tip, tone = "neutral", testid }) {
  const valueClass = tone === "warn" ? "text-[#FBA9C1]" : "text-white";
  return (
    <span
      className="inline-flex items-center gap-1.5 shrink-0"
      data-testid={testid}
      title={tip || label}
    >
      <Icon className="h-3 w-3 text-white/55" aria-hidden="true" />
      <span className="text-white/55 uppercase tracking-[0.12em] text-[10px] hidden sm:inline">{label}</span>
      <span className={`font-bold tabular-nums ${valueClass}`}>{value}</span>
    </span>
  );
}
